import {
  computed,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { IMask, ISubscriptionConfig } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ActiveSubscriptionGateway } from './active-subscriptions/active-subscriptions.gateway';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionConfigStore {
  private readonly gateway: ActiveSubscriptionGateway =
    new ActiveSubscriptionGateway();

  private readonly subscriptionId: WritableSignal<string | undefined> =
    signal(undefined);

  #subscriptionConfigResource: ResourceRef<
    IApiResponse<ISubscriptionConfig> | undefined
  > = resource({
    params: this.subscriptionId,
    loader: ({ params }: ResourceLoaderParams<string | undefined>) => {
      return firstValueFrom(this.gateway.get(params));
    },
  });

  readonly subscriptionConfig: Signal<ISubscriptionConfig | undefined> =
    computed((): ISubscriptionConfig | undefined => {
      return this.#subscriptionConfigResource.hasValue()
        ? this.#subscriptionConfigResource.value()?.data
        : undefined;
    });

  readonly masks: Signal<IMask[] | undefined> = computed(
    (): IMask[] | undefined => {
      return this.subscriptionConfig()?.masks;
    }
  );

  readonly inlineMasks: Signal<IMask[] | undefined> = computed(() => {
    const allMasks: IMask[] | undefined = this.masks() ?? [];
    if (!allMasks) return undefined;

    const followingMaskGuids = new Set<string>();

    for (const mask of allMasks) {
      for (const field of mask.fields) {
        if (field.followingMaskGuid) {
          followingMaskGuids.add(field.followingMaskGuid);
        }
      }
    }

    return allMasks.filter((m: IMask) => followingMaskGuids.has(m.id));
  });

  readonly baseMasks: Signal<IMask[] | undefined> = computed(() => {
    const allMasks: IMask[] | undefined = this.masks() ?? [];
    if (!allMasks) return undefined;
    const inline: IMask[] | undefined = this.inlineMasks() ?? [];

    const followingMaskGuids = new Set(inline.map((m: IMask) => m.id));
    return allMasks.filter((m: IMask) => !followingMaskGuids.has(m.id));
  });

  readonly isLoading: Signal<boolean> = computed(() =>
    this.#subscriptionConfigResource.isLoading()
  );

  loadSubscriptionConfiguration(uuid: string | undefined) {
    this.subscriptionId.set(uuid);
  }
}
